// components/DoctorSettings.tsx
import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Divider, Layout, Tabs, Select, Space, Checkbox, Alert, Radio } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, SettingOutlined, PrinterOutlined, AimOutlined, LockOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDoctorContext } from './DoctorContext';
import { useAuth } from './AuthContext';
import PrescriptionLayoutEditor from './PrescriptionLayoutEditor';
import { doc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { useClinicContext } from './ClinicContext';
import { useOwnerContext } from './OwnerContext';
import { buildWhatsAppLink, normalizeWhatsAppNumber } from '../utils/whatsappLink';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Header, Content } = Layout;

interface SettingsFormValues {
  receiptHeader: string;
  receiptFooter: string;
  clinicName: string;
  doctorTitle: string;
  reminderDisplayName?: string;
  reminderWhatsAppNumber?: string;
  clinicAddress: string;
  clinicPhone: string;
  consultationFee?: number;
  revisitFee?: number;
  estisharaFee?: number;
  urgentFee?: number;
  diagramType?: 'none' | 'dental' | 'body';
  target_clinic_id?: string;
  data_mode?: string;
  source_clinic_id?: string;
}

const DIAGRAM_OPTIONS = [
  { value: 'none', label: 'No Diagram (Default)' },
  { value: 'dental', label: 'Dental Teeth Chart (أسنان)' },
  { value: 'body', label: 'Physical Therapy Body Map (علاج طبيعي)' },
];

const WEEKDAY_OPTIONS = [
  { key: 'sat', label: 'السبت' },
  { key: 'sun', label: 'الأحد' },
  { key: 'mon', label: 'الإثنين' },
  { key: 'tue', label: 'الثلاثاء' },
  { key: 'wed', label: 'الأربعاء' },
  { key: 'thu', label: 'الخميس' },
  { key: 'fri', label: 'الجمعة' },
];

const COPY_MODE_SEPARATE = 'separate';
const COPY_MODE_COPY = 'copy';

const EN_DAY_ALIASES: Record<string, string> = {
  saturday: 'sat',
  sunday: 'sun',
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
};

const settingsOverrideKey = (doctorId: string | null | undefined) => `clinicSettingsOverrides_${doctorId || 'unknown'}`;

function readClinicSettingsOverrides(doctorId: string | null | undefined): Record<string, Partial<SettingsFormValues>> {
  try {
    return JSON.parse(localStorage.getItem(settingsOverrideKey(doctorId)) || '{}') || {};
  } catch (_error) {
    return {};
  }
}

function writeClinicSettingsOverride(
  doctorId: string | null | undefined,
  clinicId: string,
  values: Partial<SettingsFormValues>
) {
  if (!clinicId) return;
  const all = readClinicSettingsOverrides(doctorId);
  all[clinicId] = values;
  localStorage.setItem(settingsOverrideKey(doctorId), JSON.stringify(all));
}

function dayTokenToKey(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const lower = s.toLowerCase().replace(/\./g, '');
  const shortEn: Record<string, string> = { sat: 'sat', sun: 'sun', mon: 'mon', tue: 'tue', wed: 'wed', thu: 'thu', fri: 'fri' };
  if (shortEn[lower]) return shortEn[lower];
  for (const { key, label } of WEEKDAY_OPTIONS) {
    if (s === label || s.includes(label)) return key;
  }
  const en = EN_DAY_ALIASES[lower];
  if (en) return en;
  for (const [word, key] of Object.entries(EN_DAY_ALIASES)) {
    if (lower.includes(word)) return key;
  }
  return null;
}

function availableDaysFromFirestore(days: unknown): Set<string> {
  const keys = new Set<string>();
  const arr = Array.isArray(days)
    ? days
    : typeof days === 'string'
      ? days.split(/[,،\n]/)
      : [];
  for (const item of arr) {
    const k = dayTokenToKey(item);
    if (k) keys.add(k);
  }
  return keys;
}

interface DiagramSettingsTabProps {
  settings: any;
  loading: boolean;
  updateSettings: (s: any) => Promise<boolean>;
}

const DiagramSettingsTab: React.FC<DiagramSettingsTabProps> = ({ settings, loading, updateSettings }) => {
  const [diagramType, setDiagramType] = React.useState<'none' | 'dental' | 'body'>(
    settings?.diagramType || 'none'
  );
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setDiagramType(settings?.diagramType || 'none');
  }, [settings?.diagramType]);

  const handleSave = async () => {
    setSaving(true);
    await updateSettings({ ...settings, diagramType });
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 480 }}>
      <Title level={4}>Patient Visit Diagram</Title>
      <Text type="secondary">
        Select the interactive diagram type that will appear when viewing a patient's visit.
        Doctors can click on teeth or body regions to annotate findings and treatment notes per visit.
      </Text>
      <div style={{ marginTop: 24, marginBottom: 8 }}>
        <Text strong>Diagram Type</Text>
      </div>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Select
          value={diagramType}
          onChange={(val) => setDiagramType(val as 'none' | 'dental' | 'body')}
          options={DIAGRAM_OPTIONS}
          style={{ width: '100%' }}
          size="large"
        />
        {diagramType === 'dental' && (
          <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
            <Text>
              A 32-tooth dental chart (FDI numbering) will appear on each patient visit. Click any tooth to mark it as Normal, Problem, Treated, or Missing and add a note.
            </Text>
          </Card>
        )}
        {diagramType === 'body' && (
          <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
            <Text>
              A front/back human body map will appear on each patient visit. Click any body region to mark pain, injury, or treatment areas with notes.
            </Text>
          </Card>
        )}
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={loading || saving}
          style={{ marginTop: 8 }}
        >
          Save Diagram Setting
        </Button>
      </Space>
    </div>
  );
};

const ChangePasswordTab: React.FC = () => {
  const [pwForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string }) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      message.error('No authenticated user found. Please log in again.');
      return;
    }
    setSaving(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, values.currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, values.newPassword);
      message.success('Password changed successfully!');
      pwForm.resetFields();
    } catch (error: any) {
      if (error?.code === 'auth/wrong-password' || error?.code === 'auth/invalid-credential') {
        message.error('Current password is incorrect.');
      } else if (error?.code === 'auth/weak-password') {
        message.error('New password is too weak. Use at least 6 characters.');
      } else {
        message.error('Failed to change password. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 440 }}>
      <Title level={4}>Change Password</Title>
      <Text type="secondary">
        Enter your current password to verify your identity, then set a new password.
        The change takes effect immediately — no email will be sent.
      </Text>
      <Divider />
      <Form form={pwForm} layout="vertical" onFinish={handleChangePassword} style={{ marginTop: 8 }}>
        <Form.Item
          name="currentPassword"
          label="Current Password"
          rules={[{ required: true, message: 'Please enter your current password' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter current password"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Please enter a new password' },
            { min: 6, message: 'Password must be at least 6 characters' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter new password"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm new password"
            size="large"
          />
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            icon={<LockOutlined />}
            htmlType="submit"
            loading={saving}
            size="large"
            style={{ width: '100%' }}
          >
            Change Password
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

const DoctorSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [reminderForm] = Form.useForm();
  const reminderWhatsAppNumber = Form.useWatch('reminderWhatsAppNumber', reminderForm) || '';
  const reminderWhatsAppLink = buildWhatsAppLink(reminderWhatsAppNumber);
  const { settings, loading, updateSettings, doctorId, doctorName } = useDoctorContext();
  const { canManageOwnerFeatures } = useOwnerContext();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const navigate = useNavigate();
  const { username, logout } = useAuth();
  const { clinics, selectedClinicId, selectedClinic, loading: clinicsLoading } = useClinicContext();
  const [scheduleDayKeys, setScheduleDayKeys] = useState<Set<string>>(new Set());
  const [scheduleHours, setScheduleHours] = useState<string>('');
  const [scheduleSource, setScheduleSource] = useState<'doctor' | 'clinic-default'>('clinic-default');
  const [scheduleLoading, setScheduleLoading] = useState<boolean>(true);
  const [scheduleSaving, setScheduleSaving] = useState<boolean>(false);
  const [isClinicOwner, setIsClinicOwner] = useState<boolean>(false);
  const [antiManipulationLock, setAntiManipulationLock] = useState<boolean>(false);
  const [antiManipulationSaving, setAntiManipulationSaving] = useState<boolean>(false);

  const activeClinicId = selectedClinicId && selectedClinicId !== '__all_clinics__'
    ? selectedClinicId
    : clinics[0]?._id || '';
  const hasMultipleClinics = clinics.length > 1;
  const clinicOptions = clinics.map(clinic => ({
    value: clinic._id,
    label: clinic.name || clinic._id,
  }));

  const getClinicLabel = (clinicId?: string) => (
    clinics.find(clinic => clinic._id === clinicId)?.name || clinicId || 'Selected clinic'
  );

  React.useEffect(() => {
    if (settings) {
      const overrides = readClinicSettingsOverrides(doctorId);
      form.setFieldsValue({
        ...settings,
        ...(activeClinicId ? overrides[activeClinicId] : {}),
        target_clinic_id: activeClinicId,
        data_mode: COPY_MODE_SEPARATE,
      });
    }
  }, [form, settings, doctorId, activeClinicId]);

  React.useEffect(() => {
    reminderForm.setFieldsValue({
      reminderDisplayName: settings?.reminderDisplayName || '',
      reminderWhatsAppNumber: settings?.reminderWhatsAppNumber || '',
    });
  }, [reminderForm, settings?.reminderDisplayName, settings?.reminderWhatsAppNumber]);

  const hasValidClinic = !!activeClinicId;

  React.useEffect(() => {
    setIsClinicOwner(Boolean(doctorId && canManageOwnerFeatures));
  }, [doctorId, canManageOwnerFeatures]);

  React.useEffect(() => {
    if (!hasValidClinic) {
      setAntiManipulationLock(false);
      return undefined;
    }

    const clinicRef = doc(db, 'clinics', activeClinicId);
    const unsubscribe = onSnapshot(
      clinicRef,
      (snapshot) => {
        setAntiManipulationLock(snapshot.exists() && snapshot.data()?.antiManipulationLock === true);
      },
      () => setAntiManipulationLock(false)
    );

    return () => unsubscribe();
  }, [hasValidClinic, activeClinicId]);

  const handleAntiManipulationChange = async (checked: boolean) => {
    if (!hasValidClinic) {
      message.error('Please select one clinic first.');
      return;
    }
    if (!isClinicOwner) {
      message.error('Only the clinic owner can change this setting.');
      return;
    }

    setAntiManipulationSaving(true);
    const previous = antiManipulationLock;
    setAntiManipulationLock(checked);
    const clinicRef = doc(db, 'clinics', activeClinicId);

    try {
      await setDoc(clinicRef, { antiManipulationLock: checked }, { merge: true });
      message.success('Anti-manipulation lock saved');
    } catch (error) {
      console.error('Error saving anti-manipulation lock:', error);
      setAntiManipulationLock(previous);
      message.error('Failed to save anti-manipulation lock');
    } finally {
      setAntiManipulationSaving(false);
    }
  };

  React.useEffect(() => {
    if (!hasValidClinic || !doctorId) {
      setScheduleLoading(false);
      return undefined;
    }
    setScheduleLoading(true);
    const clinicRef = doc(db, 'clinics', activeClinicId);
    const unsubscribe = onSnapshot(
      clinicRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setScheduleDayKeys(new Set());
          setScheduleHours('');
          setScheduleSource('clinic-default');
          setScheduleLoading(false);
          return;
        }
        const data = snapshot.data() as any;
        const doctorSchedule =
          data?.doctorSchedules && typeof data.doctorSchedules === 'object'
            ? data.doctorSchedules[doctorId]
            : null;
        const isDoctorOverride =
          doctorSchedule && typeof doctorSchedule === 'object'
          && (Array.isArray(doctorSchedule.availableDays) || typeof doctorSchedule.availableHours === 'string');
        const days = isDoctorOverride ? doctorSchedule.availableDays : data.availableDays;
        const hours = isDoctorOverride ? doctorSchedule.availableHours : data.availableHours;
        setScheduleDayKeys(availableDaysFromFirestore(days));
        setScheduleHours(typeof hours === 'string' ? hours : '');
        setScheduleSource(isDoctorOverride ? 'doctor' : 'clinic-default');
        setScheduleLoading(false);
      },
      () => {
        setScheduleLoading(false);
      }
    );
    return () => unsubscribe();
  }, [hasValidClinic, doctorId, activeClinicId]);

  const handleSaveAvailability = async () => {
    if (!hasValidClinic || !doctorId) {
      message.error('Clinic or doctor is missing. Please select a clinic first.');
      return;
    }
    setScheduleSaving(true);
    const clinicRef = doc(db, 'clinics', activeClinicId);
    const daysArr = WEEKDAY_OPTIONS.filter((o) => scheduleDayKeys.has(o.key)).map((o) => o.label);
    const doctorSchedule = {
      availableDays: daysArr,
      availableHours: scheduleHours.trim(),
      updatedAt: new Date().toISOString(),
      updatedByRole: 'doctor-dashboard',
      updatedById: doctorId,
    };

    try {
      await updateDoc(clinicRef, {
        [`doctorSchedules.${doctorId}`]: doctorSchedule,
      });
      message.success('Doctor availability saved');
    } catch (error) {
      try {
        await setDoc(
          clinicRef,
          {
            doctorSchedules: {
              [doctorId]: doctorSchedule,
            },
          },
          { merge: true }
        );
        message.success('Doctor availability saved');
      } catch (fallbackError) {
        console.error('Error saving doctor availability:', fallbackError);
        message.error('Failed to save doctor availability');
      }
    } finally {
      setScheduleSaving(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: SettingsFormValues) => {
    try {
      const {
        target_clinic_id,
        data_mode,
        source_clinic_id,
        ...settingsValues
      } = values;
      const targetClinicId = target_clinic_id || activeClinicId;
      const overrides = readClinicSettingsOverrides(doctorId);
      const sourceValues = data_mode === COPY_MODE_COPY && source_clinic_id
        ? overrides[source_clinic_id] || settings
        : {};
      const valuesToSave = {
        ...sourceValues,
        ...settingsValues,
      } as SettingsFormValues;

      if (targetClinicId) {
        writeClinicSettingsOverride(doctorId, targetClinicId, valuesToSave);
      }

      const success = await updateSettings({
        ...valuesToSave,
        ...(targetClinicId ? { clinic_id: targetClinicId } : {}),
      } as any);
      if (success) {
        message.success('Settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    }
  };

  const handleReminderSettingsSave = async (values: Pick<SettingsFormValues, 'reminderDisplayName' | 'reminderWhatsAppNumber'>) => {
    if (!canManageOwnerFeatures) {
      message.error('Only the clinic owner can change the reminder name.');
      return;
    }
    if (!settings) {
      message.error('Doctor settings are still loading. Please try again.');
      return;
    }

    const rawWhatsAppNumber = String(values.reminderWhatsAppNumber || '').trim();
    const normalizedWhatsAppNumber = rawWhatsAppNumber
      ? normalizeWhatsAppNumber(rawWhatsAppNumber)
      : '';
    if (rawWhatsAppNumber && !normalizedWhatsAppNumber) {
      message.error('Please enter a valid WhatsApp number including its country code.');
      return;
    }

    const success = await updateSettings({
      ...settings,
      reminderDisplayName: String(values.reminderDisplayName || '').trim(),
      reminderWhatsAppNumber: normalizedWhatsAppNumber,
    });
    if (success) {
      message.success('WhatsApp reminder settings saved successfully');
    }
  };

  // Return to dashboard
  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        <Header style={{
          padding: 0,
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: 16
        }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            type="text"
          >
            Back to Dashboard
          </Button>
          <div style={{ marginRight: 16 }}>
            <span>Logged in as: {username}</span>
          </div>
        </Header>

        <Content style={{ margin: '24px 16px', padding: 24, background: 'white' }}>
          <Card>
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane
                tab={
                  <span>
                    <SettingOutlined />
                    General Settings
                  </span>
                }
                key="1"
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  initialValues={settings}
                >
                  {hasMultipleClinics && (
                    <>
                      <Alert
                        style={{ marginBottom: 16 }}
                        type="info"
                        showIcon
                        message={
                          <span>
                            Current branch: <strong>{selectedClinic?.name || getClinicLabel(activeClinicId)}</strong>
                          </span>
                        }
                      />
                      <Form.Item
                        name="target_clinic_id"
                        label="Branch / الفرع"
                        rules={[{ required: true, message: 'Please select the branch' }]}
                      >
                        <Select
                          loading={clinicsLoading}
                          options={clinicOptions}
                          placeholder="Select branch"
                        />
                      </Form.Item>
                      <Form.Item
                        name="data_mode"
                        label="Data setup / طريقة الإضافة"
                      >
                        <Radio.Group>
                          <Radio.Button value={COPY_MODE_SEPARATE}>Set separately</Radio.Button>
                          <Radio.Button value={COPY_MODE_COPY}>Copy from another clinic</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.data_mode !== cur.data_mode || prev.target_clinic_id !== cur.target_clinic_id}>
                        {({ getFieldValue }) => getFieldValue('data_mode') === COPY_MODE_COPY ? (
                          <Form.Item
                            name="source_clinic_id"
                            label="Copy existing settings from / نسخ البيانات من"
                            rules={[{ required: true, message: 'Please select source clinic' }]}
                          >
                            <Select
                              loading={clinicsLoading}
                              options={clinicOptions.filter(option => option.value !== getFieldValue('target_clinic_id'))}
                              placeholder="Select source clinic"
                            />
                          </Form.Item>
                        ) : null}
                      </Form.Item>
                    </>
                  )}
                  <Title level={4}>Clinic Information</Title>
                  <Form.Item
                    name="clinicName"
                    label="Clinic Name"
                    rules={[{ required: true, message: 'Please enter your clinic name' }]}
                  >
                    <Input placeholder="Enter your clinic name" />
                  </Form.Item>

                  <Form.Item
                    name="doctorTitle"
                    label="Doctor Title and Specialization"
                    rules={[{ required: true, message: 'Please enter your title and specialization' }]}
                  >
                    <Input placeholder="e.g., Dr. John Smith, Cardiologist" />
                  </Form.Item>

                  <Form.Item
                    name="clinicAddress"
                    label="Clinic Address"
                  >
                    <Input placeholder="Enter your clinic address" />
                  </Form.Item>

                  <Form.Item
                    name="clinicPhone"
                    label="Clinic Phone"
                  >
                    <Input placeholder="Enter your clinic phone number" />
                  </Form.Item>

                  <Divider orientation="left">Consultation Fees / رسوم الكشف</Divider>
                  <Form.Item name="consultationFee" label="Examination Fee (Kashf) / رسوم الكشف">
                    <Input type="number" suffix="EGP" placeholder="Example: 500" />
                  </Form.Item>
                  <Form.Item name="revisitFee" label="Re-visit Fee (Eada) / رسوم الإعادة">
                    <Input type="number" suffix="EGP" placeholder="Example: 200" />
                  </Form.Item>
                  <Form.Item name="estisharaFee" label="Consultation Fee / Consultation">
                    <Input type="number" suffix="EGP" placeholder="Example: 150" />
                  </Form.Item>
                  <Form.Item name="urgentFee" label="Urgent Fee (Mustajal) / رسوم المستعجل">
                    <Input type="number" suffix="EGP" placeholder="Example: 700" />
                  </Form.Item>

                  <Divider />

                  <Title level={4}>Receipt Header/Footer (Standard Mode)</Title>
                  <Text type="secondary">
                    These are used when "Show System Header/Footer" is enabled or when using Standard Print Mode.
                  </Text>

                  <Form.Item
                    name="receiptHeader"
                    label="Receipt Header"
                    help="This will appear at the top of every receipt. You can include welcome messages or clinic information."
                    style={{ marginTop: 16 }}
                  >
                    <TextArea
                      placeholder="Enter custom header text"
                      autoSize={{ minRows: 2, maxRows: 6 }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="receiptFooter"
                    label="Receipt Footer"
                    help="This will appear at the bottom of every receipt. You can include follow-up instructions or general advice."
                  >
                    <TextArea
                      placeholder="Enter custom footer text"
                      autoSize={{ minRows: 2, maxRows: 6 }}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      htmlType="submit"
                      loading={loading}
                    >
                      Save General Settings
                    </Button>
                  </Form.Item>
                </Form>
              </Tabs.TabPane>

              <Tabs.TabPane
                tab={
                  <span>
                    <PrinterOutlined />
                    Prescription Layout
                  </span>
                }
                key="2"
              >
                <div style={{ marginBottom: 16 }}>
                  <Text>
                    Use this editor to customize how prescriptions are printed. You can upload an image of your
                    pre-printed paper to use as a guide for setting margins and alignment.
                  </Text>
                </div>
                <PrescriptionLayoutEditor />
              </Tabs.TabPane>

              <Tabs.TabPane
                tab={
                  <span>
                    <SettingOutlined />
                    Medical History Template
                  </span>
                }
                key="3"
              >
                <div style={{ marginBottom: 16 }}>
                  <Text>
                    Customize the medical history form template that will be used when recording patient history.
                    You can add, remove, or modify sections and fields to match your practice needs.
                  </Text>
                </div>
                <Button
                  type="primary"
                  onClick={() => navigate('/history-template')}
                  style={{ marginTop: 16 }}
                >
                  Open Template Builder
                </Button>
              </Tabs.TabPane>

              <Tabs.TabPane
                tab={
                  <span>
                    <AimOutlined />
                    Specialty Diagram
                  </span>
                }
                key="4"
              >
                <DiagramSettingsTab settings={settings} loading={loading} updateSettings={updateSettings} />
              </Tabs.TabPane>

              <Tabs.TabPane
                tab={
                  <span>
                    <SettingOutlined />
                    Availability
                  </span>
                }
                key="5"
              >
                <div style={{ maxWidth: 680 }}>
                  <Title level={4}>Doctor Availability (Per Doctor)</Title>
                  <Text type="secondary">
                    This sets working days/hours for the currently logged in doctor only.
                    If no doctor-specific value exists, clinic default values are shown.
                  </Text>

                  {!hasValidClinic && (
                    <Alert
                      style={{ marginTop: 16 }}
                      type="warning"
                      showIcon
                      message="Please select one clinic first to edit availability."
                    />
                  )}

                  <div style={{ marginTop: 16 }}>
                    <Text strong>Current source: </Text>
                    <Text>{scheduleSource === 'doctor' ? 'Doctor-specific override' : 'Clinic default fallback'}</Text>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Text strong>Working Days</Text>
                    <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                      {WEEKDAY_OPTIONS.map(({ key, label }) => (
                        <Checkbox
                          key={key}
                          checked={scheduleDayKeys.has(key)}
                          disabled={scheduleLoading || !hasValidClinic}
                          onChange={(e) => {
                            setScheduleDayKeys((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(key);
                              else next.delete(key);
                              return next;
                            });
                          }}
                        >
                          {label}
                        </Checkbox>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Text strong>Working Hours</Text>
                    <Input
                      style={{ marginTop: 8 }}
                      value={scheduleHours}
                      disabled={scheduleLoading || !hasValidClinic}
                      onChange={(e) => setScheduleHours(e.target.value)}
                      placeholder="Example: 9 AM - 9 PM"
                    />
                  </div>

                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveAvailability}
                    disabled={!hasValidClinic}
                    loading={scheduleSaving}
                    style={{ marginTop: 20 }}
                  >
                    Save Doctor Availability
                  </Button>
                </div>
              </Tabs.TabPane>

              {isClinicOwner && (
              <Tabs.TabPane
                tab={
                  <span>
                    <LockOutlined />
                    Anti-manipulation
                  </span>
                }
                key="6"
              >
                <div style={{ maxWidth: 680 }}>
                  <Title level={4}>قفل التلاعب / Anti-manipulation lock</Title>
                  <Text type="secondary">
                    When enabled, the assistant portal hides queue +/− controls and emergency entry,
                    and only allows entering the current queued patient.
                  </Text>

                  {!hasValidClinic && (
                    <Alert
                      style={{ marginTop: 16 }}
                      type="warning"
                      showIcon
                      message="Please select one clinic first to edit this setting."
                    />
                  )}

                  {!isClinicOwner && (
                    <Alert
                      style={{ marginTop: 16 }}
                      type="info"
                      showIcon
                      message="Only the clinic owner can change this setting."
                    />
                  )}

                  <Card size="small" style={{ marginTop: 16 }}>
                    <Checkbox
                      checked={antiManipulationLock}
                      disabled={!hasValidClinic || !isClinicOwner || antiManipulationSaving}
                      onChange={(e) => handleAntiManipulationChange(e.target.checked)}
                    >
                      <Space direction="vertical" size={2}>
                        <Text strong>قفل التلاعب / Anti-manipulation lock</Text>
                        <Text type="secondary">
                          Default is off. Turn it on to protect queue order in the assistant portal.
                        </Text>
                      </Space>
                    </Checkbox>
                  </Card>
                </div>
              </Tabs.TabPane>
              )}

              {canManageOwnerFeatures && (
                <Tabs.TabPane
                  tab={<span><WhatsAppOutlined />WhatsApp Reminders</span>}
                  key="whatsapp-reminders"
                >
                  <div style={{ maxWidth: 680 }}>
                    <Title level={4}>WhatsApp Reminder Settings / إعدادات تذكير الواتساب</Title>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 20 }}
                      message="Owner-only clinic setting / إعداد العيادة متاح للمالك فقط"
                      description="The saved name and WhatsApp contact number are used for appointment reminders for every doctor linked to the clinic. Template 2136 automatically includes the generated WhatsApp link."
                    />
                    <Form
                      form={reminderForm}
                      layout="vertical"
                      onFinish={handleReminderSettingsSave}
                    >
                      <Form.Item
                        name="reminderDisplayName"
                        label="Doctor/Center Name / اسم الطبيب أو المركز"
                        help={`Leave blank to use the doctor booked in the appointment${doctorName ? ` (default owner name: ${doctorName})` : ''}.`}
                      >
                        <Input
                          maxLength={160}
                          allowClear
                          placeholder={doctorName || 'Doctor or center name / اسم الطبيب أو المركز'}
                        />
                      </Form.Item>
                      <Form.Item
                        name="reminderWhatsAppNumber"
                        label="WhatsApp Contact Number / رقم واتساب للتواصل"
                        help="You may enter an Egyptian local number such as 01210284654 or any international number with its country code."
                        rules={[{
                          validator: (_, value) => !value || normalizeWhatsAppNumber(String(value))
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid WhatsApp number.')),
                        }]}
                      >
                        <Input
                          inputMode="tel"
                          maxLength={24}
                          allowClear
                          placeholder="01210284654"
                        />
                      </Form.Item>
                      {reminderWhatsAppLink && (
                        <Alert
                          type="success"
                          showIcon
                          style={{ marginBottom: 16 }}
                          message="Generated WhatsApp link / رابط واتساب"
                          description={(
                            <a href={reminderWhatsAppLink} target="_blank" rel="noreferrer">
                              {reminderWhatsAppLink}
                            </a>
                          )}
                        />
                      )}
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        htmlType="submit"
                        loading={loading}
                      >
                        Save Reminder Settings / حفظ إعدادات التذكير
                      </Button>
                    </Form>
                  </div>
                </Tabs.TabPane>
              )}

              <Tabs.TabPane
                tab={
                  <span>
                    <LockOutlined />
                    Account & Security
                  </span>
                }
                key="7"
              >
                <ChangePasswordTab />
              </Tabs.TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default DoctorSettings;
